import { Eye, User } from 'lucide-react';
import {
  ADMIN_USER_STATUS_LABELS,
  getAdminUserStatus,
  mapAdminUserStatus,
  type AdminUserListDto,
} from '../types/admin-user.types';
import type { DataTableColumn, PaginationMeta } from '@/shared/components/ui/data-table';
import { DataTable } from '@/shared/components/ui/data-table';
import { StatusBadge } from '@/shared/components/ui/status-badge';
import { UnifiedButton } from '@/shared/components/ui/unified-button';
import { formatAdminDateTime } from '@/shared/utils/format-date.utils';
import { getRoleLabel } from '@/shared/utils/role-label.utils';

interface AdminUsersTableProps {
  data: AdminUserListDto[];
  pagination?: PaginationMeta;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onView?: (user: AdminUserListDto) => void;
}

export function AdminUsersTable({
  data,
  pagination,
  onPageChange,
  onPageSizeChange,
  onView,
}: AdminUsersTableProps) {
  const columns: DataTableColumn<AdminUserListDto>[] = [
    {
      key: 'id',
      header: 'ID',
      render: (row) => <span>{row.id}</span>,
      className: 'max-w-[5rem]',
    },
    {
      key: 'email',
      header: 'Email',
      render: (row) => <span>{row.email}</span>,
    },
    {
      key: 'displayName',
      header: 'Nombre',
      render: (row) => <span>{row.displayName || 'Sin nombre'}</span>,
    },
    {
      key: 'roleName',
      header: 'Rol',
      render: (row) => <span className="font-medium">{getRoleLabel(row.roleName)}</span>,
      className: 'max-w-[8rem]',
    },
    {
      key: 'status',
      header: 'Estado',
      render: (row) => {
        const { status, label } = ADMIN_USER_STATUS_LABELS[row.status]
          ? mapAdminUserStatus(row.status)
          : getAdminUserStatus(row);
        return <StatusBadge status={status} label={label} />;
      },
      className: 'max-w-[8rem]',
    },
    {
      key: 'lastLoginAt',
      header: 'Último acceso',
      render: (row) => <span>{formatAdminDateTime(row.lastLoginAt, 'Nunca')}</span>,
      className: 'max-w-[10rem]',
    },
    {
      key: 'actions',
      header: 'Acciones',
      render: (row) => (
        <div className="flex items-center gap-1">
          <UnifiedButton
            buttonType="basic"
            onClick={() => onView?.(row)}
            title="Ver detalle"
            data-testid={`admin-users-view-button-${row.id}`}
          >
            <Eye size={24} />
          </UnifiedButton>
        </div>
      ),
      className: 'max-w-[8rem]',
    },
  ];

  return (
    <div data-testid="admin-users-table">
      <DataTable
        title="Usuarios"
        titleIcon={<User className="w-4 h-4 text-primary" />}
        subtitle={`${pagination?.totalCount ?? data.length} usuarios`}
        rows={data}
        columns={columns}
        getRowKey={(row) => row.id}
        testIdPrefix="admin-users"
        pagination={pagination}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        emptyMessage={
          <div
            data-testid="admin-users-empty-state"
            className="flex flex-col items-center gap-3 py-8"
          >
            <User className="w-10 h-10 text-primary opacity-30" />
            <span className="text-primary text-sm">
              No se encontraron usuarios administradores con los filtros aplicados.
            </span>
          </div>
        }
      />
    </div>
  );
}
